import { Button, Checkbox, InputNumber, Modal, Spin, message } from "antd";
import { SafetyOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useLoaderData } from "react-router-dom";
import { BmcApiService } from "../ApiService/BmcService";
import BmcHeader from "./BmcHeader";
import PoweredByEasebuzz from "./PoweredByEasebuzz";
import "../styles/index.css";

const formatAmount = (val) =>
  Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BmcLinkPage = () => {
  const loaderData = useLoaderData();
  const token = loaderData?._token || "";
  const [messageApi, contextHolder] = message.useMessage();
  const [isPaying, setPaying] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const dueAmount = loaderData?.success ? Number(loaderData.total_outstanding_amount || 0) : 0;
  const [paymentAmount, setPaymentAmount] = useState(() => (dueAmount > 0 ? dueAmount : null));

  // Link expired or invalid
  if (!loaderData?.success) {
    return (
      <div className="bmc-wrapper">
        <div className="bmc-card">
          <BmcHeader />
          <div className="bmc-status-body">
            <div className="bmc-status-icon failure">✕</div>
            <p className="bmc-status-title failure">Link Expired</p>
            <p className="bmc-status-subtitle">
              {loaderData?.message || "This payment link has expired."}
            </p>
          </div>
          <div className="bmc-footer bmc-status-card-footer">
            For any queries, please contact us at{" "}
            <a href="mailto:contact@mcgm.gov.in">contact@mcgm.gov.in</a>
          </div>
        </div>
        <div className="bmc-status-bottom-bar">
          <span>
            For any queries, please contact us at{" "}
            <a href="mailto:contact@mcgm.gov.in">contact@mcgm.gov.in</a>
          </span>
          <PoweredByEasebuzz />
        </div>
      </div>
    );
  }

  const {
    property_account_number,
    total_outstanding_amount,
    customer_name,
    mobile_number,
    email,
    property_type,
  } = loaderData;

  const handlePay = async () => {
    if (!termsAccepted) {
      messageApi.warning("Please accept the Terms and Conditions to proceed.");
      return;
    }
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      messageApi.error("Please enter a valid payment amount.");
      return;
    }
    setPaying(true);
    const response = await BmcApiService.initiatePayment({
      property_account_number,
      property_type: property_type || "P",
      amount: paymentAmount,
      due_amount: total_outstanding_amount,
      payment_link_token: token,
    });
    setPaying(false);

    if (response?.success && response?.redirect_url) {
      window.location.href = response.redirect_url;
    } else {
      messageApi.error(response?.message || "Failed to initiate payment. Please try again.");
    }
  };

  return (
    <>
      {contextHolder}
      <div className="bmc-wrapper-dark">

        {/* Mobile-only top bar: Powered by Easebuzz */}
        <div className="bmc-mobile-topbar">
          <PoweredByEasebuzz />
        </div>

        <div className="bmc-confirm-split">

          {/* Left column: card + contact footer */}
          <div className="bmc-confirm-left-col">
            <div className="bmc-left-card">
              <BmcHeader />
              <div className="bmc-confirm-info">
                {customer_name ? (
                  <>
                    <p className="bmc-greeting">Hi {customer_name},</p>
                    <p className="bmc-greeting-sub">Please confirm your information</p>
                  </>
                ) : (
                  <p className="bmc-greeting" style={{ marginBottom: 20 }}>
                    Please confirm your payment details
                  </p>
                )}
                {mobile_number && (
                  <div className="bmc-info-row">
                    <div className="bmc-info-label">Mobile Number</div>
                    <div className="bmc-info-value">{mobile_number}</div>
                  </div>
                )}
                {email && (
                  <div className="bmc-info-row">
                    <div className="bmc-info-label">Email ID</div>
                    <div className="bmc-info-value">{email}</div>
                  </div>
                )}
                <div className="bmc-info-row">
                  <div className="bmc-info-label">Section Account Code</div>
                  <div className="bmc-info-value">{property_account_number}</div>
                </div>

                {/* Mobile-only inline contact footer */}
                <div className="bmc-mobile-info-footer">
                  For any queries, please contact us at{" "}
                  <a href="mailto:contact@mcgm.gov.in">contact@mcgm.gov.in</a>
                </div>
              </div>
            </div>
            <div className="bmc-confirm-footer-left">
              For any queries, please contact us at{" "}
              <a href="mailto:contact@mcgm.gov.in">contact@mcgm.gov.in</a>
            </div>
          </div>

          {/* Right column: card + logo footer */}
          <div className="bmc-confirm-right-col">
            <div className="bmc-right-card">
              <div className="bmc-amount-due-row">
                <span className="bmc-amount-due-label">Amount Due:</span>
                <span className="bmc-amount-due-value">
                  ₹{Number(total_outstanding_amount || 0).toLocaleString("en-IN")}
                </span>
              </div>

              {/* Payment Amount — desktop: above checkbox; mobile: reordered via CSS order */}
              <div className="bmc-payment-section">
                <div className="bmc-payment-amount-label">Payment Amount</div>
                <div className="bmc-payment-input-row">
                  <InputNumber
                    className="bmc-amount-input"
                    value={paymentAmount}
                    onChange={setPaymentAmount}
                    min={0.01}
                    precision={2}
                    placeholder={dueAmount > 0 ? formatAmount(dueAmount) : "Enter amount"}
                    controls={false}
                    size="large"
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      value != null && value !== ""
                        ? `₹ ${Number(value).toLocaleString("en-IN")}`
                        : ""
                    }
                    parser={(value) => value.replace(/[₹\s,]/g, "")}
                  />
                  {/* Mobile-only: Pay button inline with input */}
                  <Button
                    className="bmc-pay-btn-dark bmc-mobile-pay-btn"
                    size="large"
                    onClick={handlePay}
                    disabled={isPaying || !termsAccepted}
                  >
                    {isPaying ? <Spin size="small" /> : <><SafetyOutlined /> Pay</>}
                  </Button>
                </div>
              </div>

              <div className="bmc-terms-row">
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                <span>
                  I have read and agreed to{" "}
                  <span className="bmc-terms-link" style={{ cursor: "pointer" }} onClick={() => setTermsOpen(true)}>
                    Terms and conditions
                  </span>
                </span>
              </div>

              <Modal
                title="Terms and Conditions"
                open={termsOpen}
                onCancel={() => setTermsOpen(false)}
                footer={[
                  <Button key="accept" className="bmc-pay-btn-dark" onClick={() => { setTermsAccepted(true); setTermsOpen(false); }}>
                    Accept
                  </Button>,
                ]}
                width={600}
              >
                <div style={{ maxHeight: 360, overflowY: "auto", fontSize: 14, color: "#444", lineHeight: 1.7 }}>
                  <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
                  <p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
                  <p>Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.</p>
                  <p>Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet.</p>
                  <p>At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident.</p>
                </div>
              </Modal>

              {/* Desktop-only: full-width Pay button below checkbox */}
              <Button
                className="bmc-pay-btn-dark bmc-desktop-pay-btn"
                size="large"
                onClick={handlePay}
                disabled={isPaying || !termsAccepted}
              >
                {isPaying ? <Spin size="small" /> : <><SafetyOutlined /> Pay</>}
              </Button>
            </div>
            <div className="bmc-confirm-footer-right">
              <PoweredByEasebuzz />
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export const fetchBmcLinkData = async ({ request }) => {
  const url = new URL(request.url);
  const pathname = url.pathname;

  // Strict path check
  if (!pathname.includes("/protax")) {
    return { success: false, message: "This payment link is invalid." };
  }

  // Determine the search value from query string only
  // Format: /BMC/protax/?2026-06-03-TOKEN (query string)
  let search = decodeURIComponent(url.search.slice(1));

  // Detect receipt vs payment link:
  // Payment link format: 2026-06-03-TOKEN (starts with 4-digit year)
  // Receipt format: S260603075H5DB (easepayid, no date prefix)
  const isPaymentLink = /^\d{4}-\d{2}-\d{2}-.+/.test(search);

  if (search && !isPaymentLink) {
    const response = await BmcApiService.getReceiptInfo(search);
    if (response?.success && response?.transaction_id) {
      const base = (process.env.REACT_APP_LAYER_URL || "").replace(/\/$/, "");
      throw new Response(null, {
        status: 302,
        headers: { Location: `${base}/bmc_mumbai/download-receipt/?txn_id=${response.transaction_id}` },
      });
    }
    return { success: false, message: "Receipt not found." };
  }

  // Payment link: /BMC/protax/?2026-06-03-TOKEN
  const parts = search.split("-");
  if (parts.length < 4) return { success: false, message: "This payment link has expired." };
  const [year, month, day, ...tokenParts] = parts;
  const token = tokenParts.join("-");
  if (!token) return { success: false, message: "This payment link has expired." };
  const response = await BmcApiService.resolveLink(token, year, month, day);
  return response ? { ...response, _token: token } : { success: false, message: "This payment link has expired." };
};

export default BmcLinkPage;
